import type { QueryRowType } from "@rocicorp/zero";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { useCallback, useEffect, useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries, roundedHoursAgo } from "../../../shared/queries";
import { config } from "../../environment";
import { useAdminSpaceView } from "../../hooks/useAdminSpaceView";
import { useAdminViewSubject } from "../../hooks/useAdminViewSubject";
import { useCurrentUserId } from "../../hooks/useCurrentUserId";
import { useGetPresence } from "../../hooks/useGetPresence";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { isSpaceMember, useSpaces } from "../../hooks/useSpaces";
import { useUrlSort } from "../../hooks/useUrlSort";
import {
  hasLiveVersion,
  isWorkflowStale,
  WORKFLOW_INACTIVITY_DAYS,
} from "../../lib/branch";
import {
  connectorAvatarItems,
  mainBranchConnectorIds,
} from "../../lib/connector";
import {
  useMakeUrl,
  navigate,
  routes,
  useCurrentUrl,
  useRouteMatch,
} from "../../lib/urls";
import { userDisplayName } from "../../lib/userDisplayName";
import { workflowReadmeSnippet } from "../../lib/workflowReadmeSnippet";
import {
  type TriggerType,
  triggerTypeOrder,
  workflowTriggerTypes,
} from "../../lib/workflowTrigger";
import { ActivityFacepile } from "../ActivityFacepile";
import { Backdrop } from "../Backdrop";
import {
  BuildStatus,
  getBranchListStatus,
  getWorkflowListDraft,
  type StatusFilterValue,
  statusFilterOrder,
} from "../BuildStatus";
import { ConnectorAvatars } from "../ConnectorAvatars";
import { CreateWorkflow, HERO_MIN_GAP } from "../CreateWorkflow";
import { DismissibleHeroLayout } from "../DismissibleHeroLayout";
import { EmptyState } from "../EmptyState";
import { TriggerTypes } from "../TriggerTypes";
import {
  hasActiveFilters,
  ListFilter,
  ListFilterPills,
  parseMultiFilter,
} from "../ListFilter";
import { ListTable } from "../ListTable";
import { countPageCards, PagesList } from "../PagesList";
import { PagesTabHeader } from "../PagesTabHeader";
import { Text } from "../Text";
import { SmallWorkflowBadge } from "../SmallWorkflowBadge";
import { TidyUpModal } from "../TidyUpModal";
import { WorkflowActionsMenu } from "../WorkflowActionsMenu";
import { workflowListFilters } from "../workflowListFilters";
import { WorkflowNameCell } from "../WorkflowNameCell";
import { SpaceLeaderboard } from "./SpaceLeaderboard";
import { TidyUpButton } from "./TidyUpButton";

type Props = {
  spaceId: string;
};

type SpaceDetailData = NonNullable<
  QueryRowType<ReturnType<typeof queries.loadSpace>>
>;
type SpaceDetailWorkflow = SpaceDetailData["workflows"][number];
type WorkflowCreator = NonNullable<SpaceDetailWorkflow["createdBy"]>;
type ConnectorInfo = QueryRowType<ReturnType<typeof queries.connectorCatalog>>;

function getWorkflowConnectors(
  workflow: Pick<SpaceDetailWorkflow, "branches">
): string[] {
  return mainBranchConnectorIds(workflow.branches);
}

export function Space({ spaceId }: Props) {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const currentUserId = useCurrentUserId();
  const isMobile = useIsMobile();
  const activitySince = roundedHoursAgo(24);
  const [allConnectors] = useQuery(queries.connectorCatalog());
  const [spaceQuery, spaceResult] = useQuery(
    queries.loadSpace({ spaceId, activitySince })
  );
  const appsQuery = spaceQuery;
  const appsResult = spaceResult;
  const [onPersonalRoute] = useRouteMatch(routes.personal);
  const linksRoute = onPersonalRoute
    ? routes.personal.links
    : routes.space.links;
  const [isLinksTab] = useRouteMatch(linksRoute);
  const [isLeaderboardTab] = useRouteMatch(routes.space.leaderboard);
  const [rbacMembers, rbacMembersResult] = useQuery(
    isLeaderboardTab ? queries.spaceMembers({ spaceId }) : undefined
  );
  const space = spaceQuery;
  const [workflowViews] = useQuery(queries.userWorkflowViews());
  const displayName = useSpaceDisplayName();
  const adminSubject = useAdminViewSubject();
  const adminSpace = useAdminSpaceView();
  const canWriteSpace = useHasAccess("space:write", spaceId);
  const canPushLive = useHasAccess("space:push_live", spaceId);
  const [readableSpaces] = useSpaces({ settingsView: false });
  const isMember = readableSpaces.some(
    (s) => s.id === spaceId && isSpaceMember(s)
  );
  const baseHeroHeight = isMobile ? 160 : 240;
  const [heroHeight, setHeroHeight] = useState(baseHeroHeight);
  const handleFormHeightChange = useCallback(
    (height: number) => setHeroHeight(height + HERO_MIN_GAP * 2),
    []
  );

  const connectorById = new Map(allConnectors.map((a) => [a.id, a]));
  const backingAppByWorkflowId = new Map(
    allConnectors
      .filter((a) => a.backingWorkflowId && !a.fresh)
      .map((a) => [a.backingWorkflowId!, a])
  );
  const presenceMap = new Map(
    workflowViews.map((v) => [v.workflowId, v.lastSeenAt])
  );
  const { presentByWorkflow: presentUserIdsByWorkflow } = useGetPresence({
    enabled: spaceResult.type === "complete",
  });

  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    onPersonalRoute ? routes.personal : routes.space,
    {
      name: "asc",
      buildStatus: "desc",
      createdAt: "desc",
      createdBy: "asc",
    }
  );
  const {
    status: statusParam,
    app: appParam,
    creator: creatorParam,
    trigger: triggerParam,
  } = useCurrentUrl(routes.space);
  const statusFilter: StatusFilterValue | null = (
    statusFilterOrder as readonly string[]
  ).includes(statusParam)
    ? (statusParam as StatusFilterValue)
    : null;
  const triggerFilters = parseMultiFilter(triggerParam);
  const appFilters = parseMultiFilter(appParam);
  const creatorFilters = parseMultiFilter(creatorParam);
  const [cleanupModalOpen, setCleanupModalOpen] = useState(false);

  useEffect(() => {
    if (spaceResult.type !== "complete") return;
    if (!space?.workflows) {
      navigate(routes.index, undefined, { replace: true });
    }
  }, [spaceResult.type, space?.workflows]);

  const spaceWorkflows = space?.workflows ?? [];
  // Archiving a live workflow needs space:push_live, so without it the
  // cleanup only offers draft-only workflows.
  const staleWorkflows = canWriteSpace
    ? spaceWorkflows.filter(
        (w) =>
          isWorkflowStale(w) && (canPushLive || !hasLiveVersion(w.branches))
      )
    : [];

  const handleCleanup = (workflowIds: string[]) => {
    for (const workflow of staleWorkflows) {
      if (!workflowIds.includes(workflow.id)) continue;
      z.mutate(sharedMutators.deleteWorkflow({ workflowId: workflow.id }));
    }
    setCleanupModalOpen(false);
  };

  const getWorkflowBuildStatus = (workflow: SpaceDetailWorkflow) => {
    const branch = getWorkflowListDraft(workflow);
    if (!branch) return null;
    return getBranchListStatus(branch, presenceMap.get(workflow.id));
  };

  const availableStatuses = new Set<StatusFilterValue>();
  const availableAppIds = new Set<string>();
  const availableCreators = new Map<string, WorkflowCreator>();
  const availableTriggerTypes = new Set<TriggerType>();
  for (const workflow of spaceWorkflows) {
    const status = getWorkflowBuildStatus(workflow);
    if (status) availableStatuses.add(status);
    for (const type of workflowTriggerTypes(workflow.branches)) {
      availableTriggerTypes.add(type);
    }
    for (const appId of getWorkflowConnectors(workflow)) {
      availableAppIds.add(appId);
    }
    if (workflow.createdByUserId && workflow.createdBy) {
      availableCreators.set(workflow.createdByUserId, workflow.createdBy);
    }
  }
  const availableStatusOptions = statusFilterOrder.filter((s) =>
    availableStatuses.has(s)
  );
  const availableTriggerOptions = triggerTypeOrder.filter((t) =>
    availableTriggerTypes.has(t)
  );
  const availableApps = [...availableAppIds]
    .map((id) => connectorById.get(id))
    .filter((a): a is ConnectorInfo => a != null)
    .sort((a, b) => a.name.localeCompare(b.name));
  const availableCreatorOptions = [...availableCreators.values()].sort((a, b) =>
    userDisplayName(a).localeCompare(userDisplayName(b))
  );

  const filteredWorkflows = spaceWorkflows.filter((workflow) => {
    if (statusFilter !== null) {
      if (getWorkflowBuildStatus(workflow) !== statusFilter) return false;
    }
    if (
      triggerFilters.length > 0 &&
      !workflowTriggerTypes(workflow.branches).some((t) =>
        triggerFilters.includes(t)
      )
    )
      return false;
    if (appFilters.length > 0) {
      const workflowAppIds = getWorkflowConnectors(workflow);
      if (!appFilters.some((id) => workflowAppIds.includes(id))) return false;
    }
    if (creatorFilters.length > 0) {
      if (
        !workflow.createdByUserId ||
        !creatorFilters.includes(workflow.createdByUserId)
      )
        return false;
    }
    return true;
  });

  const filters = workflowListFilters({
    status: { options: availableStatusOptions, value: statusFilter },
    trigger: { options: availableTriggerOptions, values: triggerFilters },
    app: { options: availableApps, values: appFilters },
    creator: { options: availableCreatorOptions, values: creatorFilters },
  });

  const sortedWorkflows = [...filteredWorkflows].sort((a, b) => {
    if (!sortColumn) {
      const aTime = a.updatedAt;
      const bTime = b.updatedAt;
      return bTime - aTime;
    }

    let aVal: string | number;
    let bVal: string | number;

    switch (sortColumn) {
      case "name":
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
        break;
      case "createdAt":
        aVal = a.createdAt;
        bVal = b.createdAt;
        break;
      case "buildStatus": {
        const getStatusVal = (w: SpaceDetailWorkflow) => {
          const branch = getWorkflowListDraft(w);
          if (!branch) return 0;
          const status = getBranchListStatus(branch, presenceMap.get(w.id));
          const priority: Record<string, number> = {
            waiting: 3,
            ready: 2,
            building: 1,
            thinking: 1,
          };
          return (
            (priority[status ?? ""] ?? 0) * 1e15 + (branch.lastChatEntryAt ?? 0)
          );
        };
        aVal = getStatusVal(a);
        bVal = getStatusVal(b);
        break;
      }
      case "createdBy":
        aVal = (a.createdBy ? userDisplayName(a.createdBy) : "").toLowerCase();
        bVal = (b.createdBy ? userDisplayName(b.createdBy) : "").toLowerCase();
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const displayedWorkflows = sortedWorkflows;

  const hasBuildStatus = displayedWorkflows.some(
    (w) => getWorkflowBuildStatus(w) != null
  );

  if (!space?.workflows) {
    return null;
  }

  const pagesWorkflows = (appsQuery?.workflows ?? []).filter((w) =>
    w.branches.some((b) => (b.headCommit?.routes?.length ?? 0) > 0)
  );
  const pagesRows = pagesWorkflows.map((w) => ({
    workflow: w,
    space,
  }));

  return (
    <>
      <title>{`${displayName(space)} • ${config.title}`}</title>
      <Backdrop permanentNavigation>
        <DismissibleHeroLayout
          key={spaceId}
          ready={
            spaceResult.type === "complete" &&
            (!isLeaderboardTab || rbacMembersResult.type === "complete")
          }
          hero={
            adminSubject ||
            (adminSpace && !isMember && !canWriteSpace) ? undefined : (
              <CreateWorkflow
                key={spaceId}
                spaceId={spaceId}
                draftKey={spaceId}
                canWrite={canWriteSpace}
                onFormHeightChange={handleFormHeightChange}
                deferQueries={spaceResult.type !== "complete"}
              />
            )
          }
          heroHeight={heroHeight}
          tabHeader={
            <PagesTabHeader
              context={
                onPersonalRoute
                  ? { kind: "personal" }
                  : { kind: "space", spaceId }
              }
              workflowsCount={spaceWorkflows.length}
              linksCount={countPageCards(pagesRows, {
                typeFilter: null,
                favFilter: false,
                routeFavoriteSet: new Set<string>(),
              })}
            />
          }
        >
          {isLinksTab ? (
            <PagesList
              rows={pagesRows}
              linksRoute={linksRoute}
              showSpaceColumn={false}
              emptyTitle="No links"
              emptyDescription="Add a link to a step to give it a shareable URL."
              isComplete={appsResult.type === "complete"}
            />
          ) : isLeaderboardTab ? (
            <SpaceLeaderboard
              spaceId={spaceId}
              workflows={spaceWorkflows}
              pagesWorkflows={pagesWorkflows}
              extraMembers={(() => {
                if (!rbacMembers) return [];
                const members: Array<{
                  id: string;
                  name: string | null;
                  email: string;
                }> = [];
                const seen = new Set<string>();
                for (const g of rbacMembers) {
                  if (g.userGroup) {
                    for (const m of g.userGroup.roleGrantsAsScope) {
                      if (m.user && !seen.has(m.user.id)) {
                        seen.add(m.user.id);
                        members.push(m.user);
                      }
                    }
                  }
                  if (g.user && !seen.has(g.user.id)) {
                    seen.add(g.user.id);
                    members.push(g.user);
                  }
                }
                return members;
              })()}
            />
          ) : (
            <ListTable
              columns={{ activity: "fixed-activity" }}
              headers={
                isMobile
                  ? { name: "Workflow" }
                  : {
                      name: "Workflow",
                      buildStatus: hasBuildStatus ? "3B status" : "",
                      apps: "Connectors",
                      trigger: "Trigger",
                      createdAt: "Created",
                      createdBy: "Created by",
                      activity: "",
                      actions: "",
                    }
              }
              sortableColumns={isMobile ? [] : sortableColumns}
              dateColumns={isMobile ? [] : ["createdAt", "buildStatus"]}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              footer={
                hasActiveFilters(filters) ? (
                  <ListFilterPills filters={filters} />
                ) : null
              }
              headerActions={
                <>
                  {staleWorkflows.length > 0 ? (
                    <TidyUpButton
                      spaceId={spaceId}
                      workflowIds={staleWorkflows.map(
                        (workflow) => workflow.id
                      )}
                      onPress={() => setCleanupModalOpen(true)}
                    />
                  ) : null}
                  <ListFilter filters={filters} />
                </>
              }
              emptyState={
                spaceResult.type === "complete" ? (
                  spaceWorkflows.length > 0 ? (
                    <EmptyState
                      title="No matches"
                      description="Try a different filter."
                      iconName="product-workflows"
                    />
                  ) : (
                    <EmptyState
                      title="No workflows"
                      description="Workflows added to this space appear here."
                      iconName="product-workflows"
                    />
                  )
                ) : null
              }
              items={displayedWorkflows.map((workflow) => {
                const workflowBuildStatus = getWorkflowBuildStatus(workflow);
                return {
                  key: workflow.id,
                  link: makeUrl(routes.space.workflow, {
                    spaceId,
                    workflowId: workflow.id,
                  }),
                  name: (hover) => (
                    <WorkflowNameCell
                      workflow={workflow}
                      hover={hover}
                      connectedAppName={
                        backingAppByWorkflowId.get(workflow.id)?.name
                      }
                      readmeDescription={workflowReadmeSnippet(
                        workflow.branches
                      )}
                    />
                  ),
                  buildStatus: <BuildStatus status={workflowBuildStatus} />,
                  trigger: (
                    <TriggerTypes branches={workflow.branches} collapseToFit />
                  ),
                  apps: (
                    <ConnectorAvatars
                      collapseToFit
                      connected={connectorAvatarItems(
                        getWorkflowConnectors(workflow),
                        connectorById
                      )}
                    />
                  ),
                  activity: (
                    <ActivityFacepile
                      collapseToFit
                      contributors={workflow.contributors}
                      presentUserIds={presentUserIdsByWorkflow.get(workflow.id)}
                    />
                  ),
                  createdAt: (
                    <Text variant="text-small">
                      {new Date(workflow.createdAt).toLocaleDateString()}
                    </Text>
                  ),
                  createdBy: (
                    <Text
                      variant="text-small"
                      color="var(--text-primary)"
                      ellipsis
                    >
                      {workflow.createdBy
                        ? userDisplayName(workflow.createdBy)
                        : null}
                    </Text>
                  ),
                  actions: (hover) => {
                    if (!hover) return null;
                    return (
                      <div style={{ marginLeft: "auto" }}>
                        <WorkflowActionsMenu
                          workflowId={workflow.id}
                          workflowName={workflow.name}
                          spaceId={spaceId}
                          hasLiveVersion={hasLiveVersion(workflow.branches)}
                          workflowLinkHidden={
                            workflow.workflowLinkHidden ?? false
                          }
                          workflowLinkCorner={
                            workflow.workflowLinkCorner ?? null
                          }
                        />
                      </div>
                    );
                  },
                };
              })}
            />
          )}
        </DismissibleHeroLayout>
      </Backdrop>
      <TidyUpModal
        key={spaceId}
        selectable
        currentUserId={currentUserId}
        open={cleanupModalOpen}
        onClose={() => setCleanupModalOpen(false)}
        onConfirm={handleCleanup}
        itemNoun={{ singular: "workflow", plural: "workflows" }}
        inactivityDays={WORKFLOW_INACTIVITY_DAYS}
        items={staleWorkflows.map((workflow) => ({
          key: workflow.id,
          href: makeUrl(routes.space.workflow, {
            spaceId,
            workflowId: workflow.id,
          }),
          name: workflow.name,
          subtitle: `Created by ${workflow.createdBy ? userDisplayName(workflow.createdBy) : "Unknown"}`,
          createdByUserId: workflow.createdByUserId,
          badge: <SmallWorkflowBadge workflow={workflow} />,
        }))}
      />
    </>
  );
}
